import { useEffect, useState } from "react";
import { FlaskConical, RefreshCw } from "lucide-react";
import { apiFetch } from "../lib/api";

const STATUS_CLS = {
  new: "bg-amber/10 text-amber border-amber/40",
  approved: "bg-emerald/10 text-emerald border-emerald/30",
  rejected: "bg-burgundy/10 text-burgundy border-burgundy/40",
  drafted: "bg-navy/10 text-navy border-navy/30",
  published: "bg-emerald/15 text-emerald border-emerald/40",
};

const StatusBadge = ({ status }) => (
  <span className={`inline-block rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] ${STATUS_CLS[status] || "bg-ink/5 text-ink/50 border-ink/15"}`}>
    {status}
  </span>
);

export default function IdeaHistory() {
  const [ideas, setIdeas] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [mode, setMode] = useState("v1");
  const [loading, setLoading] = useState(true);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const [filter, setFilter] = useState("all");
  const [promoting, setPromoting] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([apiFetch("/admin/content-ideas"), apiFetch("/admin/content-engine/mode"), apiFetch("/admin/trend-alerts")])
      .then(([rows, m, a]) => {
        setIdeas(rows);
        setMode(m.mode);
        setAlerts(a);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const acknowledge = (id, value) => {
    apiFetch(`/admin/trend-alerts/${id}`, { method: "PATCH", body: JSON.stringify({ acknowledged: value }) }).then(load);
  };

  const promote = (id) => {
    setPromoting(id);
    apiFetch(`/admin/trend-alerts/${id}/promote`, { method: "POST", body: "{}" })
      .then(load)
      .finally(() => setPromoting(""));
  };

  const setEngineMode = (next) => {
    apiFetch("/admin/content-engine/mode", { method: "POST", body: JSON.stringify({ mode: next }) }).then(() => setMode(next));
  };

  const restore = (id) => {
    apiFetch(`/admin/content-ideas/${id}`, { method: "PATCH", body: JSON.stringify({ status: "new" }) }).then(load);
  };

  const runTest = () => {
    setTesting(true);
    setTest(null);
    apiFetch("/admin/content-engine/test", { method: "POST", body: "{}" })
      .then(setTest)
      .finally(() => setTesting(false));
  };

  const rows = filter === "all" ? ideas : ideas.filter((i) => i.vertical === filter);

  return (
    <div data-testid="idea-history-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Content Engine</p>
          <h1 className="mt-2 font-serif text-3xl text-ink">Content Idea History</h1>
          <p className="mt-2 max-w-xl text-sm text-ink/60">
            Persistent 60-day memory of every suggested, approved, rejected, drafted and published idea. Near-duplicates are blocked automatically; rejected ideas stay banned unless restored.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 border border-ink/15 bg-warmwhite px-4 py-2" data-testid="engine-mode">
            <span className="text-[10px] uppercase tracking-[0.2em] text-ink/50">Daily engine</span>
            <span className={`text-[10px] font-bold uppercase tracking-[0.15em] ${mode === "v2" ? "text-emerald" : "text-amber"}`} data-testid="engine-mode-value">{mode === "v2" ? "v2 · Editorial desk" : "v1 · Classic"}</span>
            <button onClick={() => setEngineMode(mode === "v2" ? "v1" : "v2")} className="border border-ink/20 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.15em] text-ink/70 hover:border-amber" data-testid="engine-mode-toggle">
              Switch to {mode === "v2" ? "v1" : "v2"}
            </button>
          </div>
          <button onClick={runTest} disabled={testing} className="flex items-center gap-2 border border-ink/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-ink hover:border-amber disabled:opacity-50" data-testid="run-test-generation">
            {testing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <FlaskConical className="h-3.5 w-3.5" strokeWidth={1.5} />}
            {testing ? "Generating… (up to 1 min)" : "Run v2 test (no save, no email)"}
          </button>
        </div>
      </div>

      {test && (
        <div className="mt-6 border border-amber/40 bg-amber/5 p-6" data-testid="test-results">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber">v2 test run — {test.count} ideas · {test.date} · nothing saved, nothing emailed</p>
          <div className="mt-4 space-y-4">
            {test.ideas.map((i, idx) => (
              <div key={idx} className="border border-ink/10 bg-warmwhite p-4" data-testid={`test-idea-${idx}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={i.vertical === "dental" ? "approved" : i.vertical === "consulting" ? "drafted" : "new"} />
                  <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-ink/50">{i.vertical}</span>
                  <span className="text-[9px] uppercase tracking-[0.15em] text-ink/40">{i.idea_type}</span>
                  <span className="text-[9px] uppercase tracking-[0.15em] text-ink/40">Sensitivity: {i.sensitivity}</span>
                  <span className="text-[9px] uppercase tracking-[0.15em] text-ink/40">Shelf: {i.shelf_life}</span>
                </div>
                <p className="mt-2 font-serif text-base text-ink">{i.headline}</p>
                <div className="mt-2 grid gap-1 text-xs text-ink/60 sm:grid-cols-2">
                  <p><span className="font-semibold text-ink/80">Keyword:</span> {i.keyword}</p>
                  <p><span className="font-semibold text-ink/80">Intent:</span> {i.search_intent}</p>
                  <p><span className="font-semibold text-ink/80">Why now:</span> {i.why_now}</p>
                  <p><span className="font-semibold text-ink/80">Reader:</span> {i.target_reader}</p>
                  {i.source_topic && <p><span className="font-semibold text-ink/80">Source:</span> {i.source_topic} {i.source_detected ? `(detected ${i.source_detected})` : ""}</p>}
                  <p><span className="font-semibold text-ink/80">Last similar:</span> {i.last_similar ? `${i.last_similar.headline} (${i.last_similar.date})` : "None in last 60 days"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 border border-ink/10 bg-warmwhite" data-testid="trend-alerts-panel">
        <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">
            Trend Alerts {alerts.filter((a) => !a.acknowledged).length > 0 && <span className="ml-2 rounded-full bg-burgundy px-2 py-0.5 text-[9px] font-bold text-warmwhite" data-testid="trend-unread-count">{alerts.filter((a) => !a.acknowledged).length} unread</span>}
          </p>
          <p className="text-[10px] text-ink/40">Live Malaysian conversations flagged between engine runs</p>
        </div>
        {alerts.length === 0 ? (
          <p className="px-6 py-6 text-sm text-ink/40" data-testid="trend-alerts-empty">No trend alerts yet. When a national conversation worth a sensitive reflection surfaces, it lands here and in your inbox.</p>
        ) : (
          <ul>
            {alerts.map((a) => (
              <li key={a.id} className={`border-b border-ink/5 px-6 py-4 last:border-0 ${a.acknowledged ? "opacity-60" : ""}`} data-testid={`trend-alert-${a.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {!a.acknowledged && <span className="h-2 w-2 rounded-full bg-burgundy" aria-label="unread" />}
                  <p className="font-serif text-base text-ink">{a.topic}</p>
                  <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] ${a.sensitivity === "High" ? "border-burgundy/40 bg-burgundy/10 text-burgundy" : a.sensitivity === "Medium" ? "border-amber/40 bg-amber/10 text-amber" : "border-emerald/30 bg-emerald/10 text-emerald"}`}>
                    {a.sensitivity || "Medium"} sensitivity
                  </span>
                  {a.promoted && <span className="rounded-full border border-emerald/40 bg-emerald/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-emerald">In Opportunities</span>}
                </div>
                <div className="mt-2 grid gap-1 text-xs text-ink/60 sm:grid-cols-2">
                  <p><span className="font-semibold text-ink/80">Why it matters:</span> {a.why_relevant}</p>
                  <p><span className="font-semibold text-ink/80">Reflective angle:</span> {a.angle}</p>
                  <p><span className="font-semibold text-ink/80">Detected:</span> {(a.detected_at || "").slice(0, 10)}</p>
                  <p><span className="font-semibold text-ink/80">Evergreen potential:</span> {a.evergreen_potential}</p>
                </div>
                <div className="mt-3 flex gap-2">
                  {!a.promoted && (
                    <button onClick={() => promote(a.id)} disabled={promoting === a.id} className="border border-ink/20 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.15em] text-ink hover:border-amber disabled:opacity-50" data-testid={`promote-${a.id}`}>
                      {promoting === a.id ? "Generating idea…" : "Turn into opportunity"}
                    </button>
                  )}
                  {a.acknowledged ? (
                    <button onClick={() => acknowledge(a.id, false)} className="border border-ink/10 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.15em] text-ink/40 hover:text-ink" data-testid={`unack-${a.id}`}>
                      Mark unread
                    </button>
                  ) : (
                    <button onClick={() => acknowledge(a.id, true)} className="border border-ink/10 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.15em] text-ink/60 hover:border-amber" data-testid={`ack-${a.id}`}>
                      Acknowledge
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-8 flex gap-2" data-testid="idea-filters">
        {["all", "dental", "consulting", "publishing"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`} className={`px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${filter === f ? "bg-ink text-warmwhite" : "border border-ink/15 text-ink/60 hover:text-ink"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-x-auto border border-ink/10 bg-warmwhite" data-testid="idea-history-table">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-ink/10 text-[9px] font-bold uppercase tracking-[0.2em] text-ink/50">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Facet</th>
              <th className="px-4 py-3">Idea</th>
              <th className="px-4 py-3">Keyword</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Similarity</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-ink/40">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-ink/40">No ideas recorded yet.</td></tr>
            ) : (
              rows.map((i) => (
                <tr key={i.id} className="border-b border-ink/5 last:border-0" data-testid={`idea-row-${i.id}`}>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-ink/60">{i.date}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-ink/60">{i.vertical}</td>
                  <td className="max-w-[320px] px-4 py-3 text-ink">{i.headline}</td>
                  <td className="max-w-[180px] px-4 py-3 text-xs text-ink/60">{i.keyword || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
                  <td className="max-w-[220px] px-4 py-3 text-xs text-ink/50">
                    {i.last_similar ? <span>~{Math.round((i.last_similar.score || 0) * 100)}% · {i.last_similar.headline?.slice(0, 45)} ({i.last_similar.date})</span> : <span>None in 60 days</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {i.status === "rejected" && (
                      <button onClick={() => restore(i.id)} className="border border-ink/15 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-ink/60 hover:border-amber" data-testid={`restore-${i.id}`}>
                        Restore
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
